import { isArray } from '@ember/array';
import { isEmpty, isNone, typeOf } from '@ember/utils';
import { SerializedLegacyFilter } from 'cio/types/conditions/legacy-filter';
import { safeQueryDecode } from 'cio/helpers/query';
import isEqual from 'lodash.isequal';

export type FilterCondition = 'and' | 'or';

export enum AttributeSource {
  Event = 'event_attribute',
  Customer = 'customer_attribute',
  Collection = 'collection_attribute',
  ForeignEventAttribute = 'foreign_event_attribute',
  RelationshipAttribute = 'relationship_attribute',
}

export type AttributeFilter = {
  attribute: {
    field: string;
    operator: 'eq' | 'exists';
    inverse?: boolean;
    value?: string;
  };
};

export type ObjectAttributeFilter = {
  object_attribute: {
    type_id: string;
    field: string;
    operator: 'eq' | 'exists';
    inverse?: boolean;
    value?: string;
  };
};

export type AttributeCompareFilter = {
  attribute_compare: {
    field: string;
    field_source: AttributeSource;
    operator: 'eq' | 'exists';
    inverse?: boolean;
    value?: string;
    value_source?: AttributeSource;
    value2?: string;
  };
};

export type DeviceAttributeFilter = {
  device_attribute: {
    field: string;
    operator: 'eq' | 'exists';
    inverse?: boolean;
    value?: string;
  };
};

export type SegmentFilter = {
  segment: {
    id: number;
    inverse?: boolean;
  };
};

export type NewsletterRecipientsFilter = {
  newsletter_recipients: {
    id: number;
    inverse?: boolean;
  };
};

export type NamedBitmapFilter = {
  named_bitmap: {
    type: number;
    name: string;
    inverse?: boolean;
  };
};

export type Filter =
  | AttributeFilter
  | ObjectAttributeFilter
  | DeviceAttributeFilter
  | SegmentFilter
  | NewsletterRecipientsFilter
  | NamedBitmapFilter
  | AttributeCompareFilter;

export type OrFilter = {
  or: (Filters | Filter)[];
};

export type AndFilter = {
  and: (Filters | Filter)[];
};

export type NotFilter = {
  not: Filters | Filter;
};

export type MapFilter = {
  map: Filters | Filter;
};

export type ForeignEventFilter = {
  foreign_event: Filters | Filter;
};

export type Filters = OrFilter | AndFilter | NotFilter | MapFilter | ForeignEventFilter;

export function differentFilter(a: Record<string, any> | null, b: Record<string, any> | null) {
  if (isNone(a) || isNone(b)) {
    return !(isNone(a) && isNone(b));
  }

  if (a.type !== b.type) {
    return true;
  }

  return Object.keys(a).some((key) => {
    let x = a[key];
    let y = b[key];

    if (isArray(x) && isArray(y)) {
      if (x.length !== y.length) {
        return true;
      }

      let xs = (x as any[]).sort();
      let ys = (y as any[]).sort();

      for (let i = 0; i < xs.length; i++) {
        if (xs[i] !== ys[i]) {
          return true;
        }
      }

      return false;
      // @ts-ignore typeOf isn't typed correctly and doesn't recognize "object"
      // as valid
    } else if (typeOf(x) === 'object' || typeOf(y) === 'object') {
      return !isEqual(x, y);
    } else {
      return x !== y;
    }
  });
}

export function differentFilters(a: Record<string, any>[], b: Record<string, any>[]) {
  if (!isArray(a) || !isArray(b)) {
    return a !== b;
  }

  if (a.length !== b.length) {
    return true;
  }

  for (let i = 0; i < a.length; i++) {
    if (differentFilter(a[i], b[i])) {
      return true;
    }
  }

  return false;
}

export function isEmptyFilters(filters: Filter | Filters | SerializedLegacyFilter[]): boolean {
  if (Array.isArray(filters)) {
    return isEmpty(filters);
  }

  if ('and' in filters) {
    return isEmpty(filters.and) || filters.and.every(isEmptyFilters);
  } else if ('or' in filters) {
    return isEmpty(filters.or) || filters.or.every(isEmptyFilters);
  } else if ('not' in filters) {
    return isEmptyFilters(filters.not);
  }

  return !Object.keys(filters).length;
}

export function isEmptyFiltersQuery(query?: string | null) {
  if (!query?.length) {
    return true;
  }

  let filters = safeQueryDecode(query);

  return isEmptyFilters(filters as any);
}
